import { Component, DestroyRef, HostListener, OnInit, signal } from '@angular/core';
import {
  AppConfigService,
  AppFeature,
  CMSLanguageService,
  DefaultLocale,
  LocalesArray,
} from '@markant-platform/shared/data-access';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { DialogService } from '@markant-platform/shared/dialog/feature';
import {
  catchError,
  EMPTY,
  filter,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@auth0/auth0-angular';
import {
  ConsentService,
  ConsentType,
  InsignificantChangeNoticeResponseDto,
} from '@markant-platform/consent/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { removeStorageKeysStartingWith } from '@markant-platform/shared/util-helpers';
import { UpgradeSuggestedStoragePrefix } from '@markant-platform/cartridge/model';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'markant-platform-authenticated',
  templateUrl: './authenticated.component.html',
  styleUrls: ['./authenticated.component.scss'],
})
export class AuthenticatedComponent implements OnInit {
  @HostListener('window:pageshow', ['$event'])
  onpageshow(event: PageTransitionEvent): void {
    //reload page when gets restored by bf-cache e.g. after logout
    if (event.persisted) {
      window.location.reload();
    }
  }

  $showNavigation = signal(false);
  $noPadding = signal(false);
  $isProfilePage = signal(false);
  private automaticLogoutDialogOpen = false;

  constructor(
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly translocoService: TranslocoService,
    private readonly cmsLanguageService: CMSLanguageService,
    private readonly authService: AuthService,
    private readonly dialogService: DialogService,
    private readonly userService: UserService,
    private readonly consentService: ConsentService,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef
  ) {
    userService.patchOrganizationStatus().pipe(takeUntilDestroyed()).subscribe();
    this.initializePreferredLanguage();
    if (AppConfigService.isFeatureEnabled(AppFeature.CONSENT_DIALOG_NORMAL_CHANGE)) {
      this.checkForCurrentConsent();
    }
    this.automaticLogout();
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ urlAfterRedirects }) => {
        const urlIncludes =
          !urlAfterRedirects.includes('focus-view') && !urlAfterRedirects.includes('user-administration');

        this.$showNavigation.set(urlIncludes);
        // This is temporary. When the new catalog detail is created, we should remove it.
        this.$noPadding.set(
          !urlIncludes || (urlAfterRedirects.includes('/cartridge/catalog') && urlAfterRedirects.includes('/detail'))
        );
        this.$isProfilePage.set(urlAfterRedirects.includes('user-administration'));
      });
  }

  private initializePreferredLanguage(): void {
    this.authenticatedUserService
      .getUser()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(user => (user.language ? user.language : DefaultLocale.shorthand))
      )
      .subscribe(language => {
        const isLanguageSupported = LocalesArray.map(locale => locale.shorthand).includes(language);
        const languageToUse = isLanguageSupported ? language : DefaultLocale.shorthand;

        this.translocoService.setActiveLang(languageToUse);
        this.cmsLanguageService.activeCMSLanguage = languageToUse;
        localStorage.setItem('language', languageToUse);
        document.documentElement.lang = languageToUse;
      });
  }

  private automaticLogout(): void {
    this.authService.error$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
      if (!this.automaticLogoutDialogOpen) {
        this.dialogService.showInfoDialogWithTransloco(
          'auth.sessionExpired.headline',
          'auth.sessionExpired.message',
          this.performLogout
        );
        this.automaticLogoutDialogOpen = true;
      }
    });
  }

  private performLogout = (): void => {
    this.authService.logout({
      logoutParams: {
        returnTo: AppConfigService.appConfiguration.logoutPageUrl,
      },
    });
    this.automaticLogoutDialogOpen = false;
    removeStorageKeysStartingWith(UpgradeSuggestedStoragePrefix);
  };

  private checkForCurrentConsent(): void {
    this.consentService
      .getConsentAndChangeNotices()
      .pipe(
        switchMap(consentAndChangeNotices => {
          this.handleConsentChanges(consentAndChangeNotices.insignificantChangeNotices);
          return EMPTY;
        }),
        catchError(_ => of()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private handleConsentChanges(changes: InsignificantChangeNoticeResponseDto[]): void {
    if (changes.length < 1) {
      return;
    }

    const platformTermsOfUseChanges = changes.filter(
      change => change.consentType === ConsentType.PLATFORM_TERMS_OF_USE
    );

    const platformPrivacyNoticeChanges = changes.filter(
      change => change.consentType === ConsentType.PLATFORM_PRIVACY_NOTICE
    );

    const serviceGtcChanges = changes.filter(change => change.consentType === ConsentType.SERVICE_GTC);

    const serviceDocumentChanges = changes.filter(change => change.consentType === ConsentType.SERVICE_CONDITIONS);

    this.showDialogsInGivenOrder([
      platformTermsOfUseChanges,
      platformPrivacyNoticeChanges,
      serviceGtcChanges,
      serviceDocumentChanges,
    ]);
  }

  private async showDialogsInGivenOrder(changes?: InsignificantChangeNoticeResponseDto[][]): Promise<void> {
    if (!changes || changes.length === 0) {
      return;
    }

    if (changes[0].length === 0) {
      await this.showDialogsInGivenOrder(changes.slice(1));
      return;
    }

    this.acknowledgeConsentChanges(changes[0]);
    let consentDialog;

    const isServiceConditionChange = changes[0].some(change => change.consentType === ConsentType.SERVICE_CONDITIONS);

    if (isServiceConditionChange) {
      const serviceDocsAndUrl$ = this.setServiceDocumentChangesWithUrls(changes[0]);
      const serviceDocsAndUrl = await firstValueFrom(serviceDocsAndUrl$);

      consentDialog = this.dialogService.showServiceConditionsConsentDialog(
        serviceDocsAndUrl,
        this.cmsLanguageService.activeCMSLanguage
      );
    } else {
      consentDialog = this.dialogService.showConsentDialog(changes[0], this.cmsLanguageService.activeCMSLanguage);
    }

    consentDialog?.openChange
      .pipe(
        switchMap(_ => {
          this.showDialogsInGivenOrder(changes.slice(1));
          return EMPTY;
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private async acknowledgeConsentChanges(changes: InsignificantChangeNoticeResponseDto[]): Promise<void> {
    changes.forEach(change => firstValueFrom(this.consentService.deleteInsignificantChangeNotice(change.id)));
  }

  private setServiceDocumentChangesWithUrls(
    serviceDocumentChanges: InsignificantChangeNoticeResponseDto[]
  ): Observable<(InsignificantChangeNoticeResponseDto & { url?: string })[]> {
    const serviceIdsToUrlObs: Record<string, Observable<string>> = {};
    const serviceDocsAndUrl: (InsignificantChangeNoticeResponseDto & { url?: string })[] = [];

    serviceDocumentChanges.forEach(serviceConditionChange => {
      if (serviceConditionChange.cartridgeId) {
        serviceIdsToUrlObs[serviceConditionChange.cartridgeId] = this.consentService
          .downloadServiceDocumentAsObjectUrl(serviceConditionChange.cartridgeId)
          .pipe(catchError(_ => of('')));
      }
    });

    return forkJoin(serviceIdsToUrlObs).pipe(
      switchMap(cartridgeIdsToUrls => {
        Object.keys(cartridgeIdsToUrls).forEach(cartridgeId => {
          const insignificantChangeNoticeResponse = serviceDocumentChanges.find(
            serviceDocumentChange => serviceDocumentChange.cartridgeId === cartridgeId
          );
          if (insignificantChangeNoticeResponse) {
            serviceDocsAndUrl.push({ ...insignificantChangeNoticeResponse, url: cartridgeIdsToUrls[cartridgeId] });
          }
        });

        return of(serviceDocsAndUrl);
      })
    );
  }
}
